import type { BookLibrary } from "../models/types";
import { createEmptyLibrary } from "../models/library";
import { createDemoLibrary } from "./demo-data";
import type { SortOption } from "../ui/state";

const STORAGE_KEY = "book-list-library";
const SORT_KEY = "book-list-sort-option";

/** Load library from localStorage, seeding demo data if empty in testMode */
export function loadLocalLibrary(): BookLibrary {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw) as BookLibrary;
    }
  } catch {
    // corrupted data, start fresh
  }

  // Seed demo data in testMode so there's something to browse
  const isTestMode = new URLSearchParams(window.location.search).has("testMode");
  if (isTestMode) {
    const demo = createDemoLibrary();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(demo, null, 2));
    return demo;
  }

  return createEmptyLibrary();
}

/** Save library to localStorage */
export function saveLocalLibrary(library: BookLibrary): void {
  library.lastModified = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(library, null, 2));
}

/** Export library as a downloadable JSON file */
export function exportLibraryToFile(library: BookLibrary): void {
  const blob = new Blob([JSON.stringify(library, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "library.json";
  a.click();
  URL.revokeObjectURL(url);
}

/** Import library from a JSON file */
export function importLibraryFromFile(): Promise<BookLibrary> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }
      try {
        const text = await file.text();
        const lib = JSON.parse(text) as BookLibrary;
        resolve(lib);
      } catch (err) {
        reject(err);
      }
    });
    input.click();
  });
}

/** Load sort option from localStorage */
export function loadSortOption(): SortOption {
  const val = localStorage.getItem(SORT_KEY);
  if (val === "dateRead" || val === "series" || val === "title") return val;
  return "dateRead";
}

/** Save sort option to localStorage */
export function saveSortOption(option: SortOption): void {
  localStorage.setItem(SORT_KEY, option);
}
